/**
 * ContractDetailPage
 *
 * Page for viewing contract details and managing contract actions.
 * Shows contract info, status, timeline, and available actions.
 */

import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Grid,
  Chip,
  Alert,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Snackbar,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Send as SendIcon,
  NotificationsActive as RemindIcon,
  Block as RevokeIcon,
  Download as DownloadIcon,
  History as AuditIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Business as BusinessIcon,
  Description as TemplateIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckIcon,
  Visibility as ViewedIcon,
  Edit as EditIcon,
  Cancel as DeclinedIcon,
  HourglassEmpty as PendingIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { format } from 'date-fns';
import { contractsApi, ContractStatus } from '../../services/contractsApi';
import { getErrorMessage } from '../../services/api';
import { ContractActivityTimeline } from '../../components/contracts/ContractActivityTimeline';
import { ContractDetailSkeleton } from '../../components/contracts/ContractSkeleton';

// Status configuration
const STATUS_CONFIG: Record<ContractStatus, { label: string; color: 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info'; icon: React.ReactNode }> = {
  draft: { label: 'Draft', color: 'default', icon: <EditIcon /> },
  sent: { label: 'Sent', color: 'primary', icon: <SendIcon /> },
  viewed: { label: 'Viewed', color: 'info', icon: <ViewedIcon /> },
  signed: { label: 'Signed', color: 'secondary', icon: <CheckIcon /> },
  completed: { label: 'Completed', color: 'success', icon: <CheckIcon /> },
  declined: { label: 'Declined', color: 'error', icon: <DeclinedIcon /> },
  expired: { label: 'Expired', color: 'warning', icon: <PendingIcon /> },
  revoked: { label: 'Revoked', color: 'error', icon: <RevokeIcon /> },
};

export default function ContractDetailPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();

  // Dialog states
  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [revokeDialogOpen, setRevokeDialogOpen] = useState(false);
  const [message, setMessage] = useState('Please review and sign this contract.');
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // Check if we just sent a contract
  const justSent = searchParams.get('sent') === 'true';
  const actionParam = searchParams.get('action');

  useEffect(() => {
    if (justSent) {
      setSnackbar({
        open: true,
        message: 'Contract sent successfully!',
        severity: 'success',
      });
    }
    if (actionParam === 'send') {
      setSendDialogOpen(true);
    }
  }, [justSent, actionParam]);

  // Fetch contract
  const {
    data: contract,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['contract', id],
    queryFn: () => contractsApi.get(id!),
    enabled: Boolean(id),
  });

  // Send mutation
  const sendMutation = useMutation({
    mutationFn: () => contractsApi.send(id!, { message }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contract', id] });
      setSendDialogOpen(false);
      setSnackbar({
        open: true,
        message: 'Contract sent successfully!',
        severity: 'success',
      });
    },
    onError: (err) => {
      setSnackbar({
        open: true,
        message: getErrorMessage(err),
        severity: 'error',
      });
    },
  });

  // Remind mutation
  const remindMutation = useMutation({
    mutationFn: () => contractsApi.remind(id!),
    onSuccess: () => {
      setSnackbar({
        open: true,
        message: 'Reminder sent successfully!',
        severity: 'success',
      });
    },
    onError: (err) => {
      setSnackbar({
        open: true,
        message: getErrorMessage(err),
        severity: 'error',
      });
    },
  });

  // Revoke mutation
  const revokeMutation = useMutation({
    mutationFn: () => contractsApi.revoke(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contract', id] });
      setRevokeDialogOpen(false);
      setSnackbar({
        open: true,
        message: 'Contract revoked successfully.',
        severity: 'success',
      });
    },
    onError: (err) => {
      setSnackbar({
        open: true,
        message: getErrorMessage(err),
        severity: 'error',
      });
    },
  });

  // Download handlers
  const handleDownload = async () => {
    try {
      const blob = await contractsApi.download(id!);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${contract?.contractNumber || 'contract'}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      setSnackbar({
        open: true,
        message: getErrorMessage(err),
        severity: 'error',
      });
    }
  };

  const handleDownloadAudit = async () => {
    try {
      const blob = await contractsApi.downloadAudit(id!);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${contract?.contractNumber || 'contract'}-audit.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      setSnackbar({
        open: true,
        message: getErrorMessage(err),
        severity: 'error',
      });
    }
  };

  const handleDownloadSigned = async () => {
    try {
      const blob = await contractsApi.downloadSignedDocument(id!);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `signed-${contract?.name || id}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      setSnackbar({
        open: true,
        message: getErrorMessage(err),
        severity: 'error',
      });
    }
  };

  const handleDownloadAuditTrail = async () => {
    try {
      const blob = await contractsApi.downloadAuditTrail(id!);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit-trail-${contract?.name || id}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      setSnackbar({
        open: true,
        message: getErrorMessage(err),
        severity: 'error',
      });
    }
  };

  // Loading skeleton
  if (isLoading) {
    return (
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <IconButton onClick={() => navigate('/contracts')}>
            <BackIcon />
          </IconButton>
        </Box>
        <ContractDetailSkeleton />
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{getErrorMessage(error)}</Alert>
      </Box>
    );
  }

  if (!contract) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Contract not found</Alert>
      </Box>
    );
  }

  const statusConfig = STATUS_CONFIG[contract.status];
  const canSend = contract.status === 'draft';
  const canRemind = ['sent', 'viewed'].includes(contract.status);
  const canRevoke = ['sent', 'viewed'].includes(contract.status);
  const canDownload = Boolean(contract.boldsignDocumentId);
  const canDownloadAudit = ['signed', 'completed'].includes(contract.status);

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={() => navigate('/contracts')}>
            <BackIcon />
          </IconButton>
          <Box>
            <Typography variant="h5" fontWeight={600}>
              {contract.name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {contract.contractNumber}
            </Typography>
          </Box>
        </Box>
        <Chip
          icon={statusConfig.icon as React.ReactElement}
          label={statusConfig.label}
          color={statusConfig.color}
          size="medium"
        />
      </Box>

      <Grid container spacing={3}>
        {/* Main Content */}
        <Grid item xs={12} md={8}>
          {/* Contract Info */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Contract Details
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Client
                </Typography>
                {contract.client ? (
                  <List dense disablePadding>
                    <ListItem disablePadding sx={{ py: 0.5 }}>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <PersonIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText
                        primary={`${contract.client.firstName} ${contract.client.lastName}`}
                      />
                    </ListItem>
                    <ListItem disablePadding sx={{ py: 0.5 }}>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <EmailIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText primary={contract.client.email} />
                    </ListItem>
                    {contract.client.phone && (
                      <ListItem disablePadding sx={{ py: 0.5 }}>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <PhoneIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary={contract.client.phone} />
                      </ListItem>
                    )}
                    {contract.client.company && (
                      <ListItem disablePadding sx={{ py: 0.5 }}>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <BusinessIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary={contract.client.company} />
                      </ListItem>
                    )}
                  </List>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Unknown client
                  </Typography>
                )}
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Template
                </Typography>
                {contract.template ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                    <TemplateIcon color="action" />
                    <Typography variant="body1">{contract.template.name}</Typography>
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    No template
                  </Typography>
                )}
              </Grid>
            </Grid>

            {/* Custom Field Values */}
            {contract.customFieldValues && Object.keys(contract.customFieldValues).length > 0 && (
              <>
                <Divider sx={{ my: 3 }} />
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Custom Fields
                </Typography>
                <Grid container spacing={2}>
                  {Object.entries(contract.customFieldValues).map(([key, value]) => (
                    <Grid item xs={12} sm={6} key={key}>
                      <Typography variant="body2" color="text.secondary">
                        {key}
                      </Typography>
                      <Typography variant="body1">{value || '(empty)'}</Typography>
                    </Grid>
                  ))}
                </Grid>
              </>
            )}
          </Paper>

          {/* Timeline */}
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Timeline
            </Typography>

            <List>
              <ListItem>
                <ListItemIcon>
                  <ScheduleIcon color="action" />
                </ListItemIcon>
                <ListItemText
                  primary="Created"
                  secondary={format(new Date(contract.createdAt), 'MMM d, yyyy h:mm a')}
                />
              </ListItem>

              {contract.sentAt && (
                <ListItem>
                  <ListItemIcon>
                    <SendIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Sent"
                    secondary={format(new Date(contract.sentAt), 'MMM d, yyyy h:mm a')}
                  />
                </ListItem>
              )}

              {contract.viewedAt && (
                <ListItem>
                  <ListItemIcon>
                    <ViewedIcon color="info" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Viewed"
                    secondary={format(new Date(contract.viewedAt), 'MMM d, yyyy h:mm a')}
                  />
                </ListItem>
              )}

              {contract.signedAt && (
                <ListItem>
                  <ListItemIcon>
                    <CheckIcon color="success" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Signed"
                    secondary={format(new Date(contract.signedAt), 'MMM d, yyyy h:mm a')}
                  />
                </ListItem>
              )}

              {contract.completedAt && (
                <ListItem>
                  <ListItemIcon>
                    <CheckIcon color="success" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Completed"
                    secondary={format(new Date(contract.completedAt), 'MMM d, yyyy h:mm a')}
                  />
                </ListItem>
              )}

              {contract.declinedAt && (
                <ListItem>
                  <ListItemIcon>
                    <DeclinedIcon color="error" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Declined"
                    secondary={format(new Date(contract.declinedAt), 'MMM d, yyyy h:mm a')}
                  />
                </ListItem>
              )}

              {contract.revokedAt && (
                <ListItem>
                  <ListItemIcon>
                    <RevokeIcon color="error" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Revoked"
                    secondary={format(new Date(contract.revokedAt), 'MMM d, yyyy h:mm a')}
                  />
                </ListItem>
              )}
            </List>
          </Paper>

          {/* Activity Timeline */}
          <Paper sx={{ p: 3, mt: 3 }}>
            <Typography variant="h6" gutterBottom>
              Activity Timeline
            </Typography>
            <ContractActivityTimeline contractId={id!} />
          </Paper>
        </Grid>

        {/* Sidebar - Actions */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Actions
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {canSend && (
                <Button
                  variant="contained"
                  startIcon={<SendIcon />}
                  onClick={() => setSendDialogOpen(true)}
                  fullWidth
                >
                  Send for Signature
                </Button>
              )}

              {canRemind && (
                <Button
                  variant="outlined"
                  startIcon={<RemindIcon />}
                  onClick={() => remindMutation.mutate()}
                  disabled={remindMutation.isPending}
                  fullWidth
                >
                  {remindMutation.isPending ? 'Sending...' : 'Send Reminder'}
                </Button>
              )}

              {canDownload && (
                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={handleDownload}
                  fullWidth
                >
                  Download PDF
                </Button>
              )}

              {canDownloadAudit && (
                <Button
                  variant="outlined"
                  startIcon={<AuditIcon />}
                  onClick={handleDownloadAudit}
                  fullWidth
                >
                  Download Audit Trail
                </Button>
              )}

              {contract.status === 'completed' && (
                <>
                  <Divider />
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      variant="outlined"
                      startIcon={<DownloadIcon />}
                      onClick={handleDownloadSigned}
                      sx={{ flex: 1 }}
                    >
                      Signed Doc
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<AuditIcon />}
                      onClick={handleDownloadAuditTrail}
                      sx={{ flex: 1 }}
                    >
                      Audit Trail
                    </Button>
                  </Box>
                </>
              )}

              {canRevoke && (
                <>
                  <Divider />
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<RevokeIcon />}
                    onClick={() => setRevokeDialogOpen(true)}
                    fullWidth
                  >
                    Revoke Contract
                  </Button>
                </>
              )}
            </Box>

            {/* Status-specific messages */}
            {contract.status === 'completed' && (
              <Alert severity="success" sx={{ mt: 3 }}>
                This contract has been completed. All parties have signed.
              </Alert>
            )}

            {contract.status === 'declined' && (
              <Alert severity="error" sx={{ mt: 3 }}>
                This contract was declined by the client.
              </Alert>
            )}

            {contract.status === 'revoked' && (
              <Alert severity="warning" sx={{ mt: 3 }}>
                This contract was revoked and is no longer valid.
              </Alert>
            )}

            {contract.status === 'expired' && (
              <Alert severity="warning" sx={{ mt: 3 }}>
                This contract has expired.
              </Alert>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Send Dialog */}
      <Dialog open={sendDialogOpen} onClose={() => setSendDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Send Contract for Signature</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Send this contract to {contract.client?.firstName} {contract.client?.lastName} (
            {contract.client?.email}) for signature.
          </DialogContentText>
          <TextField
            fullWidth
            label="Message to Client"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            multiline
            rows={3}
            helperText="This message will be included in the email sent to the client"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSendDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={() => sendMutation.mutate()}
            variant="contained"
            disabled={sendMutation.isPending}
            startIcon={<SendIcon />}
          >
            {sendMutation.isPending ? 'Sending...' : 'Send'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Revoke Dialog */}
      <Dialog open={revokeDialogOpen} onClose={() => setRevokeDialogOpen(false)}>
        <DialogTitle>Revoke Contract</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to revoke this contract? The client will no longer be able to sign
            it. This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRevokeDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={() => revokeMutation.mutate()}
            color="error"
            disabled={revokeMutation.isPending}
          >
            {revokeMutation.isPending ? 'Revoking...' : 'Revoke'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
